import findActiveAddressExtension from "@/lib/clients/prisma/prismaAddressExtensions";
import findActiveInsuranceInfoExtension from "@/lib/clients/prisma/prismaInsuranceInfoExtensions";
import therapistLicenseExtensions from "@/lib/clients/prisma/therapistLicenseExtensions";
import { getDatabaseUrl } from "@marble/db";
import { createQueryInstrumentationExtension } from "@marble/db/query-instrumentation";
import getLogger from "@/lib/logger";
import type { Prisma } from "@prisma/client";
import { PrismaClient } from "@prisma/client";

const logger = getLogger("lib:clients:prisma:client");

const getPrismaClient = async () => {
  try {
    const databaseUrl = await getDatabaseUrl();

    if (databaseUrl.includes("production")) {
      globalThis.IS_PRODUCTION_DB = true;
    }

    const opts: Prisma.PrismaClientOptions = {};
    opts.datasourceUrl ||= databaseUrl;

    let client = new PrismaClient(opts)
      .$extends(findActiveInsuranceInfoExtension)
      .$extends(findActiveAddressExtension)
      .$extends(therapistLicenseExtensions)
      // Row-count/duration instrumentation. `@marble/db`'s own client is
      // instrumented at its construction site, but the Next.js app builds its
      // own client here — which is the runtime that OOMs — so it needs the
      // extension applied explicitly. Kept last so it observes the final,
      // fully-extended query path.
      .$extends(createQueryInstrumentationExtension());

    logger.info("Loaded prisma client");

    return client;
  } catch (err) {
    logger.warn(err, "Error loading prisma client");
  }
};

type ExtendRet = NonNullable<Prisma.PromiseReturnType<typeof getPrismaClient>>;
type ExtendedTypes = Parameters<Parameters<ExtendRet["$transaction"]>[0]>[0];
type ExtendedPrismaClient = PrismaClient & {
  insuranceInfo: {
    findActive: ExtendedTypes["insuranceInfo"]["findActive"];
    updateActive: ExtendedTypes["insuranceInfo"]["updateActive"];
    upsertActive: ExtendedTypes["insuranceInfo"]["upsertActive"];
    createActive: ExtendedTypes["insuranceInfo"]["createActive"];
  };
  address: {
    findActive: ExtendedTypes["address"]["findActive"];
    upsertActive: ExtendedTypes["address"]["upsertActive"];
    createActive: ExtendedTypes["address"]["createActive"];
  };
  therapistLicense: {
    findPrimary: ExtendedTypes["therapistLicense"]["findPrimary"];
    findActiveLicenseForState: ExtendedTypes["therapistLicense"]["findActiveLicenseForState"];
  };
};

declare const globalThis: {
  prismaGlobal: ExtendedPrismaClient;
  IS_PRODUCTION_DB: boolean;
} & typeof global;

let prisma = {} as ExtendedPrismaClient;

if (!process.browser) {
  prisma = globalThis.prismaGlobal ?? (await getPrismaClient());
  if (process.env.NODE_ENV !== "production") {
    globalThis.prismaGlobal = prisma;
  }
}

export { ensureProductionDatabase } from "@marble/db";

export default prisma as ExtendedPrismaClient;
